import Api from 'fou-api'
import ApiSubmissions from 'fou-api/definitions/Submission'
import ApiSubmissionSummary from 'fou-api/definitions/SubmissionSummary'
import VuexErrors from 'src/vuex/modules/errors'
import Errors from 'src/utils/helper/errors'
import { TimeoutUtil } from 'src/utils/helper/timer'
import Misc from 'src/utils/helper/misc'
import { validatSubmissionJSON } from 'src/utils/helper/submissionJSONValidator'

export default {
  namespaced: true,

  state() {
    return {
      submissions: [],
      querySubmissions: [],
      submission: {},
      updateTimer: TimeoutUtil(),
      processing: false,
      pristine: true,
      queries: {
        sortBy: 'updatedAt',
        sortDirection: 'desc',
        perPage: 20
      },
      format: null,
      liveSubmissionCount: 0,
      submissionCount: 0,
      isSuccess: false,
      isLoadingLiveModal: false,
      successFamily: {
        familyName: '',
        isFamilyLive: false
      },
      familyName: '',
      isFamilyLive: false,
      updateSubmissionHideModal: '',
      isSubmissionLimitError: false,
      submissionCredibility: 'Unknown',
      fontValidationError: false,
      foundrySubmissionCount: 0,
      isCustomPackageModal: false,
      validCustomPackageLength: false,
      allowedCustomPackageLength: false,
      countFontFiles: false,
      initialSubmissionPackage: {},
      updateSubmissionApi: false,
      submissionCountingStatus: false,
      isSubmissioninProgress: false
    }
  },

  getters: {
    submission: state => {
      return state.submission
    },
    updateSubmissionApi: state => {
      return state.updateSubmissionApi
    },
    fontValidationError: state => {
      return state.fontValidationError
    },
    list: state => {
      return state.submissions
    },
    querySubList: state => {
      return state.querySubmissions
    },
    count: state => {
      return state.submissionCount
    },
    liveCount: state => {
      return state.liveSubmissionCount
    },
    processing: state => {
      return state.processing
    },
    pristine: state => {
      return state.pristine
    },
    binary(state) {
      /**
       * Get a specific binary object by id
       * which is stored in userMetaData.binaries array
       */
      return id =>
        state.submission.userMetaData.binaries.filter(b => {
          return b.binaryId === id
        })[0] || {}
    },
    queries: state => {
      return state.queries
    },
    liveSpinner: state => {
      return state.isLoadingLiveModal
    },
    isSuccessFull: state => {
      return state.isSuccess
    },
    successFamily: state => {
      return state.successFamily
    },
    modalStatus: state => {
      return state.updateSubmissionHideModal
    },
    isSubmissionLimitError: state => {
      return state.isSubmissionLimitError
    },
    submissionCredibility: state => {
      return state.submissionCredibility === 'Acceptable'
    },
    selectedRelatedFamilies: state => {
      return state.submission.userMetaData?.familyGroup.families
    },
    hasNoSubmission: state => {
      return state.foundrySubmissionCount === 0
    },
    getCustomPackageModal: state => {
      return state.isCustomPackageModal
    },
    getInitialSubmissionPackage: state => {
      return state.initialSubmissionPackage
    },
    getCountFontFiles: state => {
      return state.countFontFiles
    },
    getValidCustomPackageLength: state => {
      return state.validCustomPackageLength
    },
    getAllowedCustomPackageLength: state => {
      return state.allowedCustomPackageLength
    },
    getSubmissionCountingStatus: state => {
      return state.submissionCountingStatus
    },
    getIsSubmissionInProgress: state => {
      return state.isSubmissioninProgress
    }
  },

  mutations: {
    updatedSubmission(state) {
      state.updateSubmissionApi = true
    },
    updateFontValidationError(state, isValid) {
      state.fontValidationError = isValid
    },
    setSubmissionCredibility(state, credibility) {
      state.submissionCredibility = credibility
    },
    set(state, submission = {}) {
      const newSubmissionObj = ApiSubmissions.build(submission)
      /**
       * If submission has not userMetaData, we know that user did no changes yet.
       */
      state.pristine = newSubmissionObj.userMetaData === undefined
      /**
       * Add required keys to submission objects.
       */
      state.submission = newSubmissionObj
      /**
       * Add required keys to submission objects.
       */
      state.initialSubmissionPackage = [...newSubmissionObj.userMetaData.packages]
    },
    setAll(state, { list = [], options }) {
      /**
       * Add required keys to submission objects.
       */
      list = ApiSubmissionSummary.build(list)
      /**
       * add first level keys for sortAll compare.
       * If null, use empty string since null wont work for comparison.
       */
      list.forEach(item => {
        item.foundryName = item.foundryName || item.foundry.name || ''
        item.ownerName = item.ownerName || item.owner?.displayName || ''
        item.familyName = item.familyName || ''
      })
      /**
       * append items.
       */
      if (options.filter && options.page === 1) {
        state.querySubmissions = [...list];
      } else if (options.filter) {
        state.querySubmissions = [...state.querySubmissions, ...list];
      } else if (options.page === 1) {
        state.submissions = [...list];
      } else {
        state.submissions = [...state.submissions, ...list];
      }
    },
    setCount(state, { count = 0, options }) {
      if (options.filter) {
        state.liveSubmissionCount = count
      } else {
        state.submissionCount = count
      }
    },
    update(state, submission = {}) {
      /**
       * Update submission object,
       * as well as all submission items.
       */
      state.submission = submission
      state.submissionCredibility = submission?.foundry?.credibility
      state.submissions.some((s, idx) => {
        if (s.id === submission.id) {
          state.submissions[idx] = submission
          return true
        }
      })
    },
    updateStatus(state, submission = {}) {
      if (submission.progress) {
        state.submission.progress = submission.progress
      }
      if (submission.validationResult) {
        state.submission.validationResult = submission.validationResult
      }
      if (submission.id) {
        state.submissions.some((s, idx) => {
          if (s.id === submission.id) {
            state.submissions[idx].progress = submission.progress
            state.submissions[idx].validationResult = submission.validationResult
            return true
          }
        })
      }
    },
    clear(state, filter = 'live') {
      state.submission = {}
      state.pristine = false
      if (filter) {
        state.querySubmissions = []
        state.liveSubmissionCount = 0
      } else {
        state.submissions = []
        state.submissionCount = 0
      }
    },
    clearSubmissionsList(state) {
      state.querySubmissions = []
      state.liveSubmissionCount = 0
      state.submissions = []
      state.submissionCount = 0
    },
    submitStart(state) {
      state.processing = true
    },
    submitComplete(state) {
      state.processing = false
    },
    setFormat(state, format) {
      state.format = format
    },
    resetFormat(state) {
      state.format = null
    },
    resetQueries(state) {
      state.queries = {
        sortBy: 'updatedAt',
        sortDirection: 'desc',
        perPage: 20
      }
    },
    resetAscQueries(state) {
      state.queries = {
        sortBy: 'updatedAt',
        sortDirection: 'asc',
        perPage: 20
      }
    },
    sortAll(state, sortBy) {
      state.queries.sortBy = sortBy
      function compareValues(key, order = 'asc') {
        return function (a, b) {
          if (!a.hasOwnProperty(key) || !b.hasOwnProperty(key)) {
            // property doesn't exist on either object
            return 0
          }
          const varA = typeof a[key] === 'string' ? a[key].toUpperCase() : a[key]
          const varB = typeof b[key] === 'string' ? b[key].toUpperCase() : b[key]
          let comparison = 0
          if (varA > varB) {
            comparison = 1
          } else if (varA < varB) {
            comparison = -1
          }
          return order.toLowerCase() === 'desc' ? comparison * -1 : comparison
        }
      }
      state.submissions.sort(compareValues(state.queries.sortBy, state.queries.sortDirection))
    },
    buildSkuData(state, data) {
      state.submission.userMetaData.packages = data
    },
    setLiveSpinner(state, val) {
      state.isLoadingLiveModal = val
    },
    setIsSuccess(state, obj = { familyName: '', isFamilyLive: false }) {
      state.isSuccess = true
      state.successFamily = obj
    },
    unsetIsSuccess(state) {
      state.isSuccess = false
      state.successFamily = {
        familyName: '',
        isFamilyLive: false
      }
    },
    onUpdtSubmsnHideModal(state, val) {
      state.updateSubmissionHideModal = val
    },
    setLimitError(state) {
      state.isSubmissionLimitError = true
    },
    resetLimitError(state) {
      state.isSubmissionLimitError = false
    },
    resetFamilyGroup(state) {
      state.submission.userMetaData.familyGroup = {
        id: 0,
        families: []
      }
    },
    setFoundrySubmissionCount(state, submissionCount) {
      state.foundrySubmissionCount = submissionCount || 0
    },
    setCustomPackageModal(state, val) {
      state.isCustomPackageModal = val
    },
    setCountFontFiles: (state, val) => {
      state.countFontFiles = val
      return state.countFontFiles
    },
    setValidCustomPackageLength: (state, val) => {
      state.validCustomPackageLength = val
      return state.validCustomPackageLength
    },
    setAllowedCustomPackageLength: (state, val) => {
      state.allowedCustomPackageLength = val
      return state.allowedCustomPackageLength
    },
    setSubmissionCountingStatus: (state, val) => {
      state.submissionCountingStatus = val
      return state.submissionCountingStatus
    },
    setIsSubmissionInProgress: (state, val) => {
      state.isSubmissioninProgress = val
      return state.isSubmissioninProgress
    }
  },

  actions: {
    updateFontValidationError({ commit }, isFontInvalid) {
      commit('updateFontValidationError', isFontInvalid)
    },
    async create({ commit }, submission = {}) {
      try {
        const response = await Api.Submissions.createSubmission(submission);
        const submissionResult = response.data.success ? response.data.submission : undefined;
        return submissionResult;
      } catch (error) {
        commit('errors/add', Errors.create(error));
        return;
      }
    },
    set({ commit }, options) {
      options = {
        submission: {},
        force: false,
        ...options
      }
      return new Promise(resolve => {
        commit('set', options.submission)
        resolve(options.submission)
      })
    },
    get({ commit, state }, options) {
      options = {
        id: null,
        force: false,
        ...options
      }
      return new Promise(resolve => {
        if (state.submission.id === options.id && !options.force) {
          /**
           * Dont call API if already has id.
           */
          resolve(state.submission)
        } else {
          /**
           * GET.
           */
          Api.Submissions.getSubmission(options.id, state.format)
            .then(response => {
              const submission = typeof response?.data?.id === 'string' ? response.data : undefined
              resolve(submission)
            })
            .catch(error => {
              commit('errors/add', Errors.create(error))
              resolve()
            })
        }
      })
    },
    getAll({ commit, state }, options = {}) {
      return new Promise((resolve, reject) => {
        Api.Submissions.getSubmissions(
          state.queries.sortBy,
          state.queries.sortDirection,
          options.filter,
          options.search,
          options.page * 1,
          state.queries.perPage * 1
        )
          .then(response => {
            const list = response.data.collection
            commit('setAll', { list, options })
            const count = response.data.metadata.count
            commit('setCount', { count, options })
            resolve(response.data)
          })
          .catch(error => {
            const err = Errors.create(error)
            commit('errors/add', err)
            reject(err)
          })
      })
    },
    update({ commit, state }, submission) {
      if (validatSubmissionJSON(state.submission.userMetaData, state.submission.description)) {
        const err = Errors.create(Misc.scriptErrorObj());
        commit('errors/add', err);
        return Promise.resolve(false); // Return a resolved Promise with `false` for consistency
      }
      return new Promise(resolve => {
        submission = typeof submission === 'object' ? submission : state.submission
        /**
         * Set processing state when updating submission (for confirmation page)
         */
        commit('submitStart')
        /**
         * Update to API.
         */
        Api.Submissions.updateSubmission(submission.id, submission)
          .then(response => {
            /**
             * After API call progress and validation values can be different (by BE).
             * Therefore update states.
             */
            if (typeof response?.data?.submission === 'object') {
              commit('update', response.data.submission)
            }
            commit('submitComplete')
            commit('onUpdtSubmsnHideModal', 'hide')
            commit('updatedSubmission')
            resolve(response)
          })
          .catch(error => {
            commit('errors/add', Errors.create(error))
            commit('submitComplete')
            resolve()
          })
      })
    },
    updateStatus({ commit }, submission = {}) {
      return new Promise(resolve => {
        commit('updateStatus', submission)
        resolve()
      })
    },
    reset({ commit }) {
      return new Promise(resolve => {
        commit('submitComplete')
        commit('clear')
        resolve()
      })
    },
    triggerBinaryValidation({ commit }, options = {}) {
      return new Promise(resolve => {
        Api.Submissions.reTriggerValidation(options.submissionId)
          .then(response => {
            resolve(response.data)
          })
          .catch(error => {
            commit('errors/add', Errors.create(error))
            resolve()
          })
      })
    },
    resetFamilyGroup({ commit }) {
      commit('resetFamilyGroup')
    },
    getInReviewOrApprovedOrLiveSubmissionCount({ commit, state }, options = {}) {
      return new Promise(resolve => {
        commit('setSubmissionCountingStatus', true)
        Api.Submissions.getInReviewOrApprovedOrLiveSubmissionCount(options?.foundryId)
          .then(response => {
            commit('setFoundrySubmissionCount', response.data.submissionCount)
            commit('setSubmissionCountingStatus', false)
            resolve(response.data)
          })
          .catch(error => {
            commit('setSubmissionCountingStatus', false)
            commit('errors/add', Errors.create(error))
            resolve(error)
          })
      })
    },
    async deleteSubmission({ commit }, submissionId) {
      try {
        const response = await Api.Submissions.deleteFamilySubmission(submissionId);
        return response.data;
      } catch (error) {
        commit('errors/add', Errors.create(error));
        return;
      }
    },
  },

  modules: {
    errors: VuexErrors
  }
}
